import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { AlertTriangle, X, Check, Loader } from 'lucide-react';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'warning' | 'danger' | 'info';
  isLoading?: boolean;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning',
  isLoading = false
}) => {
  const { currentTheme } = useTheme();

  if (!isOpen) return null;

  const typeStyles = {
    warning: {
      iconColor: '#f59e0b',
      iconBg: '#fef3c7',
      confirmBg: currentTheme.colors.primary[600],
      confirmHover: currentTheme.colors.primary[700]
    },
    danger: {
      iconColor: '#ef4444',
      iconBg: '#fecaca',
      confirmBg: '#ef4444',
      confirmHover: '#dc2626'
    },
    info: {
      iconColor: '#3b82f6',
      iconBg: '#dbeafe',
      confirmBg: currentTheme.colors.primary[600],
      confirmHover: currentTheme.colors.primary[700]
    }
  };

  const styles = typeStyles[type];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div 
        className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl"
        style={{ backgroundColor: currentTheme.colors.background.card }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div 
              className="p-2 rounded-lg mr-3"
              style={{ backgroundColor: styles.iconBg }}
            >
              <AlertTriangle 
                className="h-6 w-6" 
                style={{ color: styles.iconColor }}
              />
            </div>
            <h2 
              className="text-lg font-semibold"
              style={{ color: currentTheme.colors.text.primary }}
            >
              {title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Message */}
        <div className="mb-6">
          <p 
            className="text-sm leading-relaxed"
            style={{ color: currentTheme.colors.text.secondary }}
          >
            {message}
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center"
            style={{ backgroundColor: styles.confirmBg }}
            onMouseEnter={(e) => !isLoading && (e.currentTarget.style.backgroundColor = styles.confirmHover)}
            onMouseLeave={(e) => !isLoading && (e.currentTarget.style.backgroundColor = styles.confirmBg)}
          >
            {isLoading ? (
              <>
                <Loader className="h-4 w-4 animate-spin mr-2" />
                Processing...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                {confirmText}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};